import { datadogRum } from "@datadog/browser-rum";
import { Component, type ErrorInfo } from "react";

import { LS_KEYS } from "@core/core.constants";

import classes from "./ErrorBoundary.module.css";
import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
} from "./ErrorBoundary.types";
import { UncaughtError } from "../UncaughtError/UncaughtError";

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps | Readonly<ErrorBoundaryProps>) {
    super(props);
    this.state = { hasError: false, chunkError: false };
  }

  static getDerivedStateFromError = (error: Error) => ({
    hasError: true,
    chunkError: error.message.includes("fetch dynamically imported module"),
  });

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { pathname } = this.props;

    if (prevProps.pathname !== pathname) {
      this.setState(() => ({ hasError: false }));
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { chunkError } = this.state;

    if (!chunkError) {
      const email = localStorage.getItem(LS_KEYS.EMAIL) || "";

      datadogRum.setUser({
        id: email,
        email,
        name: email,
      });
      datadogRum.addError(new Error("error boundary ecms"), {
        error,
        errorInfo,
      });
    }
  }

  render() {
    const { hasError, chunkError } = this.state;
    const { children, fallbackHeader } = this.props;

    if (hasError) {
      return (
        <div className={classes.container}>
          {fallbackHeader}
          <div className={classes.content}>
            <UncaughtError isChunkError={chunkError} />
          </div>
        </div>
      );
    }

    return children;
  }
}
